import React from 'react';
import { Redirect, Route, Switch, useParams, useRouteMatch, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { LoadingIndicator, Breadcrumbs } from '@ygtechnologies/bygr-frontend-component-library';
import { ButtonBar, Tabs } from '@ygtechnologies/rubus-ui';
import { BuyerOptionPresetGroupInfo } from '../BuyerOptionPresetGroupInfo/BuyerOptionPresetGroupInfo';
import { BuyerOptionPresetMatrix } from '../BuyerOptionPresetMatrix';
import { useOptionPresetGroups, useOptionPresetGroupUpdate } from '@/services/option-preset/option-preset-group';
import type { OptionPresetGroupDto } from '@/services/option-preset/option-preset-group';
import { useGetActiveProjectId } from '@/utils/hooks';
import HistoryLogTable from '../../HistoryLog/HistoryLogTable';
import { Trans } from '@lingui/react/macro';
interface BuyerOptionPresetGroupPageProps { }

export const BuyerOptionPresetGroupPage: React.FC<BuyerOptionPresetGroupPageProps> = () => {
    const { groupId } = useParams<{ groupId: string }>();
    const projectId = useGetActiveProjectId();
    const { path, url } = useRouteMatch();
    const location = useLocation();

    // Determine active tab based on current path
    const getActiveTab = () => {
        if (location.pathname.endsWith('/matrix')) return 'matrix';
        if (location.pathname.endsWith('/styles')) return 'styles';
        if (location.pathname.endsWith('/history-log')) return 'history-log';
        return 'info'; // default to info
    };

    const activeTab = getActiveTab();

    const { data: groups, refetch } = useOptionPresetGroups(projectId!);
    const presetGroup = groups?.find((g) => g.id === parseInt(groupId!));
    const updateMutation = useOptionPresetGroupUpdate();

    const handleGroupUpdate = async (updatedGroup: OptionPresetGroupDto) => {
        try {
            await updateMutation.mutateAsync([
                updatedGroup.id,
                {
                    ...updatedGroup,
                },
            ]);
            refetch();
        } catch (error) {
            console.error('Failed to update preset group:', error);
            throw error;
        }
    };

    const handlePresetUpdate = () => {
        refetch();
    };

    if (!presetGroup) {
        return <LoadingIndicator />;
    }
    return (
        <>
            <Breadcrumbs>
                <Breadcrumbs.Link to={`/projects/${projectId}/options/styles/Main`}>
                    <Trans>Styles</Trans>
                </Breadcrumbs.Link>
                <Breadcrumbs.Item>{presetGroup.name}</Breadcrumbs.Item>
            </Breadcrumbs>

            <ButtonBar>
                <Tabs activeId={activeTab} variant="pill" size="sm">
                    <Tabs.Tab id="info" as={Link} to={`${url}/info`} label={<Trans>Info</Trans>} />
                    <Tabs.Tab id="matrix" as={Link} to={`${url}/matrix`} label={<Trans>Matrix</Trans>} />
                    <Tabs.Tab
                        id="history-log"
                        as={Link}
                        to={`${url}/history-log`}
                        label={<Trans>History log</Trans>}
                    />
                </Tabs>
            </ButtonBar>
            <Switch>
                <Route
                    exact
                    path={`${path}/info`}
                    render={() => <BuyerOptionPresetGroupInfo group={presetGroup} onSave={handleGroupUpdate} onPresetUpdate={handlePresetUpdate} />}
                />
                <Route
                    exact
                    path={`${path}/matrix`}
                    render={({ match }) => <BuyerOptionPresetMatrix presetGroup={presetGroup} />}
                />
                <Route
                    exact
                    path={`${path}/history-log`}
                    render={() => (
                        <div style={{ paddingTop: '16px' }}>
                            <HistoryLogTable
                                filter={{
                                    projectId: projectId!,
                                    objectType: 'OptionPresetGroup',
                                    objectIds: [presetGroup.id],
                                }}
                            />
                        </div>
                    )}
                />

                <Redirect to={`${path}/info`} />
            </Switch>
        </>
    );
};

export default BuyerOptionPresetGroupPage;
